import {
  formatParisDateTime,
  LEGACY_MIGRATION_STATUS,
  type LegacyMigrationListItem,
} from '@flux/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import type { JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchLegacyMigrations } from './api.js';
import { Dash } from './detail-parts.js';
import { useDetailSheetParam } from './detail-sheet.js';
import { MigrationDetailSheet } from './migration-detail-sheet.js';
import {
  CLEAR_MIGRATION_FILTERS,
  hasActiveMigrationFilters,
  MigrationFilters,
  readMigrationFilters,
} from './migration-filters.js';
import { migrationComposition, migrationPriceChange, migrationTier } from './migration-format.js';
import { formatSignedEuros } from './money.js';
import {
  isRechargeTokenMissing,
  RECHARGE_NOT_CONFIGURED_EMPTY_STATE,
  RechargeModeBanner,
  useRechargeSummary,
} from './recharge-status.js';

const PAGE_SIZE = 50;
/** Triables côté serveur (`apps/server/src/http/api/recharge/migrations.ts::SORT_FIELDS`). */
const SORTABLE_COLUMNS = ['createdAt', 'subscriptionId', 'status', 'difference'] as const;
const NUMERIC_COLUMNS = ['price', 'difference', 'tier'] as const;
const DEFAULT_SORT = 'createdAt';
const PRICE_WARNING_LABEL = 'Prix des items à vérifier';

const helper = createDataTableColumnHelper<LegacyMigrationListItem>();

const columns = helper.columns([
  helper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Date',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.accessor('subscriptionId', {
    id: 'subscriptionId',
    header: 'Abonnement',
    cell: (info) => <span className="tabular-nums">{info.getValue()}</span>,
  }),
  helper.accessor('email', {
    id: 'email',
    header: 'Client',
    cell: (info) => {
      const email = info.getValue();
      return email === null ? <Dash /> : <span className="break-all">{email}</span>;
    },
  }),
  helper.accessor('status', {
    id: 'status',
    header: 'Statut',
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-1.5">
        <span title={row.original.reason ?? undefined}>
          <StatusBadge registry={LEGACY_MIGRATION_STATUS} code={row.original.status} />
        </span>
        {row.original.priceWarning ? (
          <span
            role="img"
            aria-label={PRICE_WARNING_LABEL}
            title={PRICE_WARNING_LABEL}
            className="inline-flex text-status-orange-fg"
          >
            <TriangleAlert className="size-4" aria-hidden="true" />
          </span>
        ) : null}
      </span>
    ),
  }),
  helper.display({
    id: 'composition',
    header: 'Composition',
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">{migrationComposition(row.original)}</span>
    ),
  }),
  helper.display({
    id: 'price',
    header: 'Prix',
    cell: ({ row }) => {
      const price = migrationPriceChange(row.original);
      return price === null ? <Dash /> : <span className="whitespace-nowrap tabular-nums">{price}</span>;
    },
  }),
  helper.accessor('difference', {
    id: 'difference',
    header: 'Écart',
    cell: (info) => {
      const difference = formatSignedEuros(info.getValue());
      return difference === null ? (
        <Dash />
      ) : (
        <span className="whitespace-nowrap tabular-nums">{difference}</span>
      );
    },
  }),
  helper.display({
    id: 'tier',
    header: 'Palier',
    cell: ({ row }) => {
      const tier = migrationTier(row.original);
      return tier === null ? <Dash /> : <span className="whitespace-nowrap tabular-nums">{tier}</span>;
    },
  }),
]);

/**
 * Onglet Migrations legacy de l'écran Abonnements (`06` §5, §8.3) : en lecture
 * seule, les abonnements legacy réactivés et leur conversion en bundle.
 * Filtres (« Ignoré » exclu par défaut), tri et page dans l'URL, détail dans
 * un panneau porté par `?id=`.
 */
export function MigrationsTab(): JSX.Element {
  const rawSearch: unknown = useSearch({ strict: false });
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const { id, openDetail, closeDetail } = useDetailSheetParam();
  const summary = useRechargeSummary();
  const filters = readMigrationFilters(rawSearch);
  const isFiltered = hasActiveMigrationFilters(filters);

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
    ...(filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.subscriptions.migrations(listParams),
    queryFn: () => fetchLegacyMigrations(listParams),
    placeholderData: keepPreviousData,
  });

  function resetFilters(): void {
    setFilters(CLEAR_MIGRATION_FILTERS);
  }

  return (
    <>
      <RechargeModeBanner feature="legacyMigration" />
      <DataTable
        caption="Migrations des abonnements legacy"
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        sortBy={sortBy ?? DEFAULT_SORT}
        sortDir={sortDir}
        sortableColumnIds={SORTABLE_COLUMNS}
        numericColumnIds={NUMERIC_COLUMNS}
        onPageChange={setPage}
        onSortChange={setSort}
        onRowClick={(row) => {
          openDetail(row.id);
        }}
        isLoading={query.isPending}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError ? query.error.message : 'Impossible de charger les migrations.'
        }
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        toolbar={<MigrationFilters value={filters} onChange={setFilters} />}
        emptyState={
          isFiltered
            ? {
                title: 'Aucun résultat pour ces filtres',
                description: 'Élargissez la période, modifiez la recherche ou retirez un filtre.',
                action: (
                  <Button type="button" variant="outline" onClick={resetFilters}>
                    Réinitialiser les filtres
                  </Button>
                ),
              }
            : isRechargeTokenMissing(summary.data)
              ? RECHARGE_NOT_CONFIGURED_EMPTY_STATE
              : {
                  title: 'Aucune migration',
                  description:
                    'Les abonnements legacy réactivés par les clients seront convertis et listés ici.',
                }
        }
      />
      <MigrationDetailSheet id={id} onClose={closeDetail} />
    </>
  );
}
